import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { GitMerge, User, Mail, Phone, Check } from "lucide-react";

export default function DuplicateMerge() {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeDialog, setMergeDialog] = useState(null);
  const [merging, setMerging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clients/duplicates`);
      setDuplicates(res.data.duplicates || []);
    } catch (e) {  }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleMerge = async (keepId, mergeId) => {
    setMerging(true);
    try {
      const res = await axios.post(`${API}/clients/merge?keep_id=${keepId}&merge_id=${mergeId}`);
      if (res.data.success) {
        toast.success('Clients merged successfully');
        setMergeDialog(null);
        load();
      } else {
        toast.error(res.data.error || 'Merge failed');
      }
    } catch (e) { toast.error('Merge failed'); }
    setMerging(false);
  };

  return (
    <div className="space-y-6 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Duplicate Clients</h1>
          <p className="text-sm text-muted-foreground">{duplicates.length} potential duplicate{duplicates.length !== 1 ? 's' : ''} found</p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-xl">Refresh</Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : duplicates.length === 0 ? (
        <Card className="p-8 rounded-2xl text-center">
          <Check size={40} className="mx-auto text-green-500 mb-3" />
          <h3 className="font-semibold" style={{ fontFamily: 'Playfair Display' }}>No duplicates found</h3>
          <p className="text-sm text-muted-foreground">Your client list is clean!</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {duplicates.map((dup, i) => (
            <Card key={i} className="p-4 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]">
              <div className="flex items-center gap-2 mb-3">
                <GitMerge size={16} className="text-primary" />
                <Badge variant="outline" className="text-xs rounded-full">
                  Matched by: {dup.match_type === 'email' ? '📧 Email' : dup.match_type === 'phone' ? '📱 Phone' : '👤 Name'} — <span className="font-mono">{dup.match_value}</span>
                </Badge>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {[dup.client_a, dup.client_b].map((client, ci) => (
                  <div key={ci} className="p-3 bg-muted/30 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                        <User size={14} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{client.name}</p>
                        <p className="text-xs text-muted-foreground">{client.status}</p>
                      </div>
                    </div>
                    <div className="space-y-1 text-xs">
                      {client.email && <p className="flex items-center gap-1"><Mail size={10} /> {client.email}</p>}
                      {client.phone && <p className="flex items-center gap-1"><Phone size={10} /> {client.phone}</p>}
                      <p>Appointments: {client.total_appointments || 0} | Spent: £{(client.total_spent || 0).toFixed(2)}</p>
                      {client.last_appointment && <p>Last visit: {client.days_since} days ago</p>}
                    </div>
                    <Button 
                      variant="secondary" size="sm" 
                      className="mt-2 rounded-xl text-xs w-full"
                      onClick={() => setMergeDialog({ keep: client, merge: [dup.client_a, dup.client_b].find(c => c.id !== client.id) })}
                    >
                      Keep this client
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Merge confirmation dialog */}
      <Dialog open={!!mergeDialog} onOpenChange={() => setMergeDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>Confirm Merge</DialogTitle>
          </DialogHeader>
          {mergeDialog && (
            <div className="space-y-3">
              <p className="text-sm">
                Keep <strong>{mergeDialog.keep.name}</strong> and merge data from <strong>{mergeDialog.merge.name}</strong>.
              </p>
              <div className="bg-yellow-50 p-3 rounded-xl text-xs text-yellow-800">
                <p className="font-medium">What happens:</p>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  <li>All appointments will be moved to {mergeDialog.keep.name}</li>
                  <li>Missing contact info will be filled from {mergeDialog.merge.name}</li>
                  <li>Notes and tags will be merged</li>
                  <li>{mergeDialog.merge.name} will be deleted</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMergeDialog(null)} className="rounded-xl">Cancel</Button>
            <Button 
              onClick={() => handleMerge(mergeDialog.keep.id, mergeDialog.merge.id)} 
              disabled={merging}
              className="rounded-xl"
            >
              {merging ? 'Merging...' : 'Confirm Merge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
